import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Maximize2Icon, XIcon } from 'lucide-react';
import type { PlanImage } from '../data/catio';
interface ZoomableImageProps {
  image: PlanImage;
  /** Classes for the <img> inside the trigger. */
  imgClassName?: string;
  className?: string;
  tone?: 'light' | 'dark';
}

/**
 * Tap to open a full-screen view. Wide technical images get a larger-than-
 * screen width on phones so tables and dimensions become legible (scroll/pan).
 */
export function ZoomableImage({
  image,
  imgClassName = '',
  className = '',
  tone = 'light'
}: ZoomableImageProps) {
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wide = image.width / image.height > 1.3;
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);
  return <>
      <button ref={triggerRef} type="button" onClick={() => setOpen(true)} aria-label={`Enlarge: ${image.alt}`} className={`group relative block w-full cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/40 ${className}`} data-id="element-376" data-mp="2xcxi1op55fx4">
        <img src={image.src} alt={image.alt} width={image.width} height={image.height} loading="lazy" decoding="async" className={imgClassName} data-id="element-377" data-mp="2sdunwzap8u3x" />
        <span className={`absolute bottom-2.5 right-2.5 flex h-9 w-9 items-center justify-center rounded-full shadow-sm transition-transform duration-150 ease-out group-hover:scale-105 ${tone === 'dark' ? 'bg-ivory text-ink' : 'bg-ink/85 text-ivory'}`} aria-hidden data-id="element-378" data-mp="1h0a06ckeg5x2">
          <Maximize2Icon className="h-4 w-4" data-id="element-379" data-mp="39ui2fvzohv5z" />
        </span>
      </button>

      {typeof document !== 'undefined' && createPortal(<AnimatePresence data-id="element-380" data-mp="2zxn4pfb8bysr">
            {open && <motion.div role="dialog" aria-modal="true" aria-label={image.alt} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-50 flex flex-col bg-ink/95" onClick={() => setOpen(false)} data-id="element-381" data-mp="2zzgc4wto2n1t">
                <div className="flex shrink-0 items-center justify-between gap-4 px-4 py-3" data-id="element-382" data-mp="k692fks7h1lz">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ivory/60" data-id="element-383" data-mp="rqtdn8aljxzi">
                    {wide ? 'Swipe to pan' : 'Real CATIO READY™ page'}
                  </p>
                  <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Close" className="flex h-11 w-11 items-center justify-center rounded-full bg-ivory/10 text-ivory transition-colors duration-150 ease-out hover:bg-ivory/20 focus:outline-none focus-visible:ring-4 focus-visible:ring-ember-light/50" data-id="element-384" data-mp="11gczmmtlw7yl">
                    <XIcon className="h-5 w-5" aria-hidden data-id="element-385" data-mp="io0n5798nqaq" />
                  </button>
                </div>
                <div className="flex-1 overflow-auto overscroll-contain px-4 pb-6" data-id="element-386" data-mp="npzrokgpst99">
                  <motion.img initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} src={image.src} alt="" onClick={event => event.stopPropagation()} className={`mx-auto rounded-[3px] bg-white ${wide ? 'w-[max(100%,880px)] max-w-none' : 'w-full max-w-3xl'}`} data-id="element-387" data-mp="1u48rne88dbp1" />
                </div>
              </motion.div>}
          </AnimatePresence>, document.body)}
    </>;
}